import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import EditIcon from "@mui/icons-material/Edit";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import dayjs from "dayjs";

import DataTable from "../../../components/common/datatable/DataTable";
import ButtonComponent from "../../../components/common/button/Button";

export const DataDetails = (
  {data,
  rowSelection,
  handleCellValueChange,
  onSelectionChanged,
  pagination,
  paginationPageSize,
  paginationPageSizeSelector,
  gridApiRef: parentGridApiRef,
  setRowsData,
}) => {
  const [isGridReady, setIsGridReady] = useState(false);
  const [columnState, setColumnState] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState("");
  const [disableFilterBtn, setDisableFilterBtn] = useState(true);

  const isInvalidValue = (value) => {
    return value === null || value === undefined || value === "";
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    return dayjs(timestamp).format("DD-MMM-YYYY HH:mm:ss");
  };

  const renderExpandableText = (params, maxLength = 50) => {
    const text = params.value || ""; // The error details text
    const isLongText = text.length > maxLength;

    // Initialize the showFullText property for the row if not already set
    if (!params.node.showFullText) {
      params.node.showFullText = false;
    }

    const toggleText = () => {
      params.node.showFullText = !params.node.showFullText;
      params.api.refreshCells({ rowNodes: [params.node], force: true }); // Refresh the cell to update the UI
    };

    return isLongText ? (
      <div>
        {params.node.showFullText ? (
          <>
            <span>{text}</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              Less
            </button>
          </>
        ) : (
          <>
            <span>{text.substring(0, maxLength)}...</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              More
            </button>
          </>
        )}
      </div>
    ) : (
      <span>{text}</span>
    );
  };

  const columnData = useMemo(() => {
    if (data?.packagingPMMaintenanceDTOList) {
      return [
        {
          headerName: 'Status Flag',
          field: "status",
          colId: "statusIcon",
          width: 150,
          sortable: false,
          resizable: true,
          unSortIcon: true,
          pinned: "left",
          cellRenderer: (params) => {
            const status = params.value;
            let circleColor = "#999"; // default gray

            if (status === "Published") {
              circleColor = "#4CAF50"; // green
            } else if (status === "Data Error") {
              circleColor = "#F44336"; // red
            } else if (status === "Ready To Publish") {
              circleColor = "#FF9800"; // orange
            }

            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <FiberManualRecordIcon
                  style={{
                    fontSize: "16px",
                    color: circleColor,
                  }}
                  title={status}
                />
              </div>
            );
          },
        },
        {
          headerName: "Last Updated Time",
          field: "lastUpdateTimestamp",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          filter: "agSetColumnFilter",
          valueFormatter: (params) => formatTimestamp(params.value),
          filterParams: {
            valueFormatter: (params) => formatTimestamp(params.value),
          },
        },
        {
          headerName: "Year (year_pckg)",
          field: "year",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, year: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Plant (Plant No.)",
          field: "plantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, plantId: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Division",
          field: "division",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                division: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Plant Name",
          field: "plantName",
          width: 200,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          cellEditor: "agTextCellEditor",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
        },
        {
          headerName: "Supplier Pin (Supplier item No.)",
          field: "supplierMaterialNumber",
          width: 230,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                supplierMaterialNumber: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Material Type",
          field: "materialType",
          width: 230,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                materialType: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Material Number (cc_material_no)",
          field: "material",
          width: 230,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, material: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Material Description (material_description)",
          field: "materialDescription",
          width: 240,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
        },
        {
          headerName: "Vendor Number (vendor_number)",
          field: "vendorNumber",
          width: 210,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                vendorNumber: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Supplier Name (supplier_name)",
          field: "vendorName",
          width: 200,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
        },
        {
          headerName: "Location Name (vendor_location)",
          field: "supplierLocation",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          width: 220,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, supplierLocation: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Manager Name (manager_name)",
          field: "managerName",
          width: 210,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, managerName: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Supplier Description",
          field: "supplierDescription",
          width: 210,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, supplierDescription: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Effective Start Date (DD-MM-YYYY)",
          field: "validFrom",
          width: 230,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, validFrom: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Effective End Date (DD-MM-YYYY)",
          field: "validTo",
          width: 230,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, validTo: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "From Qty (from_qty)",
          field: "fromQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, fromQty: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "To Qty (to_qty)",
          field: "toQty",
          width: 180,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, toQty: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Net Price (price)",
          field: "netPrice",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, netPrice: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Curr (currency)",
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, currency: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "UOM (UOM)",
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, uom: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Per Qty (per_qty)",
          field: "perQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, perQty: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Incoterm (incoterms)",
          field: "incoterms",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, incoterms: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Inco. Loc (named_place)",
          field: "namedPlace",
          width: 220,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, namedPlace: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "MOQ Comments (comments)",
          field: "comments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          width: 220,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, comments: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Payment Description (payment_terms_description)",
          field: "paymentTermsDescription",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, paymentTermsDescription: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Pay Terms (sap_payment_term)",
          field: "paymentTerms",
          width: 230,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                paymentTerms: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Allocation Qty (allocation_qty)",
          field: "targetQuantity",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                targetQuantity: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Allocation Notes (allocation_notes)",
          field: "allocationNotes",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                allocationNotes: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Lead Time (manufacturing_lead_time)",
          field: "leadTime",
          width: 270,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, leadTime: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Last Change MM-DD-YY",
          field: "lastChange",
          width: 270,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, lastChange: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Reason for Change",
          field: "reasonForChange",
          width: 270,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, reasonForChange: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Contract Updated (Y/N)",
          field: "contractUpdated",
          width: 270,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, contractUpdated: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "User Id",
          field: "userId",
          width: 180,
          sortable: true,
          resizable: true,
          unSortIcon: true,
        },
        {
          headerName: "User Name",
          field: "userName",
          width: 180,
          sortable: true,
          resizable: true,
          unSortIcon: true,
        },
        {
          headerName: "Errors",
          field: "errors",
          width: 180,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          cellRenderer: (params) => renderExpandableText(params, 50),
        },
        {
          headerName: "Status",
          field: "status",
          width: 180,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          pinned: "right",
        },
      ];
    } else if (data?.cepgPMMaintenanceDTOList) {
      return [
        {
          headerName: 'Status Flag',
          field: "status",
          colId: "statusIcon",
          width: 150,
          sortable: false,
          resizable: true,
          unSortIcon: true,
          pinned: "left",
          cellRenderer: (params) => {
            const status = params.value;
            let circleColor = "#999"; // default gray

            if (status === "Published") {
              circleColor = "#4CAF50"; // green
            } else if (status === "Data Error") {
              circleColor = "#F44336"; // red
            } else if (status === "Ready To Publish") {
              circleColor = "#FF9800"; // orange
            }

            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <FiberManualRecordIcon
                  style={{
                    fontSize: "16px",
                    color: circleColor,
                  }}
                  title={status}
                />
              </div>
            );
          },
        },
        {
          headerName: "GMP Lead",
          field: "GMPLead",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, GMPLead: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Proc Org",
          field: "procurementGroup",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                procurementGroup: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Year (year_pckg)",
          field: "year",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, year: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Division",
          field: "division",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                division: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Plant (plant_id)",
          field: "plantId",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, plantId: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Plant Name",
          field: "plantName",
          width: 200,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          cellEditor: "agTextCellEditor",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
        },
        {
          headerName: "Material Number (Material)",
          field: "material",
          width: 230,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, material: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Material Description (Description)",
          field: "materialDescription",
          width: 240,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          cellEditor: "agTextCellEditor",
          cellClassRules: {
              "red-cell": (params) => isInvalidValue(params.value),
            },
        },
        {
          headerName: "S-Code",
          field: "sCode",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                sCode: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Change Date",
          field: "changeDate",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                changeDate: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Vendor Number (vendor_number)",
          field: "vendorNumber",
          width: 210,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                vendorNumber: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Supplier Name (supplier_name)",
          field: "vendorName",
          width: 200,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          cellEditor: "agTextCellEditor",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
        },
        {
          headerName: "Manager",
          field: "manager",
          width: 210,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                manager: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Package Size",
          field: "packSize",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                packSize: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Net Price (price)",
          field: "netPrice",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, netPrice: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Curr (currency)",
          field: "currency",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, currency: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "UOM (UOM)",
          field: "uom",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, uom: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Incoterm (incoterms)",
          field: "incoterms",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, incoterms: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Inco. Loc (named_place)",
          field: "namedPlace",
          width: 220,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, namedPlace: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "From Qty (from_qty)",
          field: "fromQty",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, fromQty: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "To Qty (to_qty)",
          field: "toQty",
          width: 180,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, toQty: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Effective Start Date (DD-MM-YYYY)",
          field: "validFrom",
          width: 230,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, validFrom: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Effective End Date (DD-MM-YYYY)",
          field: "validTo",
          width: 230,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, validTo: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Pay Terms (sap_payment_term)",
          field: "paymentTerms",
          width: 230,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                paymentTerms: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Lead Time (manufacturing_lead_time)",
          field: "leadTime",
          width: 270,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, leadTime: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Target Qty (Allocation Qty Kg)",
          field: "targetQuantity",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                targetQuantity: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Change Date",
          field: "systemDateStamp",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                systemDateStamp: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "MOQ Comments (comments)",
          field: "comments",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          width: 220,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = { ...params.data, comments: params.newValue };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Last Updated Time",
          field: "lastUpdateTimestamp",
          sortable: true,
          resizable: false,
          unSortIcon: true,
          filter: "agSetColumnFilter",
          valueFormatter: (params) => formatTimestamp(params.value),
          filterParams: {
            valueFormatter: (params) => formatTimestamp(params.value),
          },
        },
        {
          headerName: "Allocation Notes (allocation_notes)",
          field: "allocationNotes",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                allocationNotes: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "Picasso Facility",
          field: "picassoFacility",
          sortable: true,
          resizable: true,
          unSortIcon: true,
          editable: true,
          cellEditor: "agTextCellEditor",
          cellClass: "cpsComments-cell",
          cellClassRules: {
            "red-cell": (params) => isInvalidValue(params.value),
          },
          valueSetter: (params) => {
            if (params.newValue !== params.oldValue) {
              const dataCopy = {
                ...params.data,
                picassoFacility: params.newValue,
              };
              params.node.setData(dataCopy);
              return true;
            }
            return false;
          },
          cellRenderer: (params) => {
            return (
              <div className="cps-comments-icon">
                <span style={{ flex: 1, padding: "0px 4px" }}>
                  {params.value}{" "}
                </span>
                <EditIcon fontSize="sm" />
              </div>
            );
          },
        },
        {
          headerName: "User ID",
          field: "userId",
          sortable: true,
          resizable: true,
          unSortIcon: true,
        },
        {
          headerName: "User Name",
          field: "userName",
          sortable: true,
          resizable: true,
          unSortIcon: true,
        },
        {
          headerName: "Errors",
          field: "errors",
          width: 180,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          cellRenderer: (params) => renderExpandableText(params, 50),
        },
        {
          headerName: "Status",
          field: "status",
          width: 180,
          sortable: true,
          resizable: true,
          unSortIcon: true,
          pinned: "right",
        },
      ];
    }
  }, [data]);

  const selectionColumnDef = useMemo(
    () => ({
      pinned: "left",
    }),
    [],
  );

  const gridApiRef = useRef(null);

  const onColumnChange = (e) => {
    const col = e.target.value;
    setSelectedColumn(col);
    if (col && gridApiRef.current) {
      gridApiRef.current.ensureColumnVisible(col);
    }
  };

  const onGridReady = (params) => {
    gridApiRef.current = params.api; // Store the grid API instance locally
    // Also set the parent component's gridApiRef if passed as prop
    if (parentGridApiRef) {
      parentGridApiRef.current = params.api;
    }
    setIsGridReady(true);
    if (columnState) {
      params.columnApi.applyColumnState({
        state: columnState,
        applyOrder: true,
      });
    }
  };

  useEffect(() => {
    // Restore filter model from localStorage on component mount
    if (isGridReady && gridApiRef?.current) {
      const savedFilterModel = localStorage.getItem(
        "priceMemoManagementFilterModel",
      );
      if (savedFilterModel) {
        gridApiRef.current.setFilterModel(JSON.parse(savedFilterModel));
        setDisableFilterBtn(true); // Enable the "Clear All Filters" button
      }
    }
  }, [isGridReady]);

  const clearAllFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null); // Clear all filters
      localStorage.removeItem("priceMemoManagementFilterModel");
      setDisableFilterBtn(true);
    }
  };

  const onFilterChanged = () => {
    if (gridApiRef.current) {
      const filterModel = gridApiRef.current.getFilterModel();
      setDisableFilterBtn(Object.keys(filterModel).length === 0); // Enable button if filters exist
      localStorage.setItem(
        "priceMemoManagementFilterModel",
        JSON.stringify(filterModel),
      );
      if (setRowsData) {
        setRowsData(gridApiRef.current.getDisplayedRowCount());
      }
    }
  };

  const onColumnMoved = useCallback((params) => {
    if (
      params &&
      params.columnApi &&
      typeof params.columnApi.getColumnState === "function"
    ) {
      setColumnState(params.columnApi.getColumnState());
    }
  }, []);

  const gridOptions = {
    getRowId: (params) => {
      return `${params?.data?.sequenceId}`;
    },
    overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
    overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
    defaultColDef: {
      sortable: true,
      filter: "agMultiColumnFilter",
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.5,
        display: "flex",
        alignItems: "center",
      },
      valueGetter: (params) => {
          if (!params.data) return "";

          const field = params.colDef.field;
          const value = params.data[field];

          return value === null || value === undefined || value === "" ? "" : value;
      },
    },
    onColumnMoved,
    onFilterChanged,
    getContextMenuItems: () => {
      return ['copy', 'copyWithHeaders', 'paste', 'separator', 'autoSizeAll', 'resetColumns'];
    },
  };

  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} placement="left-start"/>
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
    },
  }));

  const handleExportToExcel = () => {
    if (!gridApiRef.current) return;

    // Get all displayed columns currently in the grid
    const allColumns = gridApiRef.current.getAllDisplayedColumns();

    // Filter out the columns you want to exclude
    const columnsForExport = allColumns
      .filter(col => col.getColId() !== 'statusIcon')
      .map(col => col.getColId()); // Get just the IDs

    // Call the export API with the specific column keys
    gridApiRef.current.exportDataAsExcel({
      columnKeys: columnsForExport,
    });
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          marginBottom: "20px",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
            <InputLabel>Jump to Column</InputLabel>
            <Select
              id="col-select"
              value={selectedColumn}
              label="Jump to Column"
              onChange={onColumnChange}
            >
              <MenuItem value="">Select Column</MenuItem>
              {columnData?.map((col) => (
                <MenuItem key={col?.field} value={col?.field}>
                  {col?.headerName || col?.field}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ButtonComponent
            label="Export to Excel"
            bgColor="black"
            color="white"
            onClick={handleExportToExcel}
          />
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <HtmlTooltip
            title={
              <>
                <p style={{fontSize: '15px', fontWeight: 'bold'}}>Status Brief</p>
                <p style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                  <span>
                  <FiberManualRecordIcon
                    style={{
                      fontSize: "16px",
                      color: "#4CAF50",
                    }}
                    title="Published"
                  />
                  </span> Published – The line item is live and published to Data Analysts for Contract Creation.
                </p>
                <p style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                  <span>
                  <FiberManualRecordIcon
                    style={{
                      fontSize: "16px",
                      color: "#F44336",
                    }}
                    title="Data Error"
                  />
                  </span> Data Error - The Line item has failed Data Validation which needs attention. Check the Error Details column to understand the details of the Data Error.
                </p>
                <p style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                  <span>
                  <FiberManualRecordIcon
                    style={{
                      fontSize: "16px",
                      color: "#FF9800",
                    }}
                    title="Ready To Publish"
                  />
                  </span> Ready To Publish - The line item is complete with all the data, has passed the Data Validation check. It is ready to be Published to the Data Analysts for Creating Contracts.
                </p>
                <p style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                  <span>
                  <FiberManualRecordIcon
                    style={{
                      fontSize: "16px",
                      color: "#999",
                    }}
                    title="In Draft"
                  />
                  </span> In Draft - The line item is saved with partial information with One or more Blank Mandatory fields.
                </p>
              </>
            }
          >
          <InfoIcon />
          </HtmlTooltip>
          <ButtonComponent
            maxWidth="150px"
            label="Clear All Filters"
            bgColor="black"
            color="white"
            disabled={disableFilterBtn}
            onClick={clearAllFilters}
          />
        </div>
      </div>
      <DataTable
        columns={columnData}
        rows={data?.packagingPMMaintenanceDTOList || data?.cepgPMMaintenanceDTOList || []}
        paginationProp={pagination}
        paginationPageSize={paginationPageSize}
        paginationPageSizeSelector={paginationPageSizeSelector}
        selectionColumnDef={selectionColumnDef}
        rowSelection={rowSelection}
        onSelectionChanged={onSelectionChanged}
        gridOptions={gridOptions}
        onCellValueChanged={handleCellValueChange}
        onGridReady={onGridReady}
      />
    </>
  );
};
